import { createContext, useContext } from "react";
import type { Id } from "../../convex/_generated/dataModel";

export type Session = {
  householdId: Id<"households">;
  memberId: Id<"members">;
  displayName: string;
  /**
   * The household's IANA zone, not the device's.
   *
   * Here rather than fetched per screen because the calendar needs it on every
   * render, and because "which zone does this household read in" is the same
   * kind of fact as "which household is this" — one answer, one subscription.
   * `format.ts` still defaults to the device zone for tasks, deliberately; the
   * calendar is the caller that passes this instead.
   */
  timezone: string;
};

/**
 * Three states, and they are genuinely different:
 *
 *  - `undefined` — not resolved yet. The Clerk session or `households.me` is
 *    still in flight, and screens treat it exactly like a query that hasn't
 *    landed, which is what it is.
 *  - `null` — signed in, but a member of no household. The join screen.
 *  - a `Session` — signed in and in a household. The app.
 *
 * Collapsing the first two would flash "you need an invite" at everyone on
 * every cold start, which is why they are not one value.
 */
export const SessionContext = createContext<Session | null | undefined>(undefined);

/**
 * The household to read and the member to write as.
 *
 * Held in context rather than fetched per screen so there is one subscription.
 * The provider lives in `components/SessionProvider` — split from this file so
 * neither exports a component alongside a non-component, which is what
 * `react-refresh` is checking for.
 *
 * Screens below the gate in `__root.tsx` only ever see a resolved `Session`;
 * the loading and join states are handled once, above them.
 */
export function useSession() {
  return useContext(SessionContext);
}
